import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, downloadFile } from '../../utils/api';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, X, Edit2, Plus, UserPlus, Download, Users, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, RefreshCw, Image, UserCheck, Check, ExternalLink, XCircle } from 'lucide-react';

const AttendanceCalendar = () => {
  // URL search params for park_id
  const [searchParams] = useSearchParams();
  const urlParkId = searchParams.get('park_id');
  
  // View mode: 'worker', 'supervisor', 'corrections', or 'pending-worker'
  const [viewMode, setViewMode] = useState(() => searchParams.get('tab') || 'worker');
  
  const [zones, setZones] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [parks, setParks] = useState([]);
  
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedPark, setSelectedPark] = useState('');
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateDetails, setDateDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Manual Edit States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  
  // Manual Add Attendance States (for not-visited dates)
  const [showAddAttendanceModal, setShowAddAttendanceModal] = useState(false);
  const [parkEmployees, setParkEmployees] = useState([]);
  const [addAttendanceDate, setAddAttendanceDate] = useState(null);
  const [addingAttendance, setAddingAttendance] = useState(false);
  const [manualAttendanceList, setManualAttendanceList] = useState([]);
  const [parkSupervisor, setParkSupervisor] = useState(null);
  const [existingAttendance, setExistingAttendance] = useState([]);
  
  // Supervisor Attendance Calendar States
  const [supervisorCalendarData, setSupervisorCalendarData] = useState(null);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const [supervisorMonth, setSupervisorMonth] = useState(new Date());
  
  // Worker download states
  const [downloadingWorker, setDownloadingWorker] = useState(false);
  const [downloadZone, setDownloadZone] = useState('all');
  const [downloadSupervisor, setDownloadSupervisor] = useState('all');
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [workerDownloadProgress, setWorkerDownloadProgress] = useState(0);
  const [workerDownloadStartTime, setWorkerDownloadStartTime] = useState(null);
  const [backgroundDownloadTaskId, setBackgroundDownloadTaskId] = useState(null);
  const [backgroundDownloadStatus, setBackgroundDownloadStatus] = useState(null);
  
  // Bulk Edit States
  const [showBulkEditSection, setShowBulkEditSection] = useState(false);
  const [bulkEditZone, setBulkEditZone] = useState('all');
  const [bulkEditStartDate, setBulkEditStartDate] = useState('');
  const [bulkEditEndDate, setBulkEditEndDate] = useState('');
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState(0);
  const [bulkDownloadStartTime, setBulkDownloadStartTime] = useState(null);
  const [bulkDownloadElapsed, setBulkDownloadElapsed] = useState(0);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [bulkUploadStartTime, setBulkUploadStartTime] = useState(null);
  const [bulkUploadElapsed, setBulkUploadElapsed] = useState(0);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  
  // Worker download elapsed time state
  const [workerDownloadElapsed, setWorkerDownloadElapsed] = useState(0);
  
  // Manual Supervisor Attendance Modal States
  const [showSupervisorAttendanceModal, setShowSupervisorAttendanceModal] = useState(false);
  const [selectedSupervisorForAttendance, setSelectedSupervisorForAttendance] = useState(null);
  const [selectedSupervisorDate, setSelectedSupervisorDate] = useState(null);
  const [supervisorAttendanceStatus, setSupervisorAttendanceStatus] = useState('P');
  const [supervisorAttendanceRemarks, setSupervisorAttendanceRemarks] = useState('');
  const [savingSupervisorAttendance, setSavingSupervisorAttendance] = useState(false);
  
  // Corrections View States
  const [correctionsData, setCorrectionsData] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [correctionsMonth, setCorrectionsMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [correctionsFilterSupervisor, setCorrectionsFilterSupervisor] = useState('');
  const [correctionsFilterDate, setCorrectionsFilterDate] = useState('');
  const [correctionsFilterZone, setCorrectionsFilterZone] = useState('');
  const [correctionsFilterVerified, setCorrectionsFilterVerified] = useState('unverified'); // '', 'verified', 'unverified' - default to pending
  const [correctionsFilterOptions, setCorrectionsFilterOptions] = useState({ dates: [], zones: [], supervisors: [] });
  const [correctionsCounts, setCorrectionsCounts] = useState({ total: 0, verified: 0, unverified: 0 });
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [downloadingCorrections, setDownloadingCorrections] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [viewingCorrectionId, setViewingCorrectionId] = useState(null); // Track which correction row is being viewed
  const [correctionsSortBy, setCorrectionsSortBy] = useState('date'); // 'date', 'zone', 'supervisor'
  const [collapsedDates, setCollapsedDates] = useState({}); // Track collapsed date sections
  const [viewingWorkerPhotos, setViewingWorkerPhotos] = useState(null); // { employee_id, employee_name } to view all photos
  
  // Rejected Corrections state
  const [rejectedCorrections, setRejectedCorrections] = useState([]);
  const [showRejectedDropdown, setShowRejectedDropdown] = useState(false);

  // Pending Worker Attendance States
  const [pendingWorkerMonth, setPendingWorkerMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pendingWorkerData, setPendingWorkerData] = useState(null);
  const [pendingWorkerLoading, setPendingWorkerLoading] = useState(false);
  const [pendingWorkerExpandedParks, setPendingWorkerExpandedParks] = useState({});
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  // Sort function for numeric strings (lowest to highest)
  const sortNumeric = (arr) => {
    return [...arr].sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return String(a).localeCompare(String(b));
    });
  };

  // Sort parks alphabetically by name
  const sortParksAlphabetically = (parksArr) => {
    return [...parksArr].sort((a, b) => 
      (a.park_name || '').localeCompare(b.park_name || '')
    );
  };

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  // Initial fetch - runs once
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchZones();
    fetchSupervisors();
  }, [authLoading, user, userType]);

  // Auto-load park from URL param
  useEffect(() => {
    const loadParkFromUrl = async () => {
      if (!urlParkId || authLoading || !user || userType !== 'admin') return;
      
      try {
        // Switch to Worker Attendance view when loading park from URL
        setViewMode('worker');
        
        // Reset state first to show loading
        setCalendarData(null);
        setLoading(true);
        
        // Fetch park details to get zone and division
        const response = await adminAPI.getParkDetails(urlParkId);
        const park = response.data.park;
        
        if (park) {
          // Set zone
          setSelectedZone(park.zone);
          
          // Fetch divisions for this zone
          const divResponse = await adminAPI.getDivisions(park.zone);
          setDivisions(sortNumeric(divResponse.data.divisions || []));
          setSelectedDivision(park.division);
          
          // Fetch parks for this zone/division
          const parkResponse = await adminAPI.getParksList(park.zone, park.division);
          setParks(sortParksAlphabetically(parkResponse.data.parks || []));
          setSelectedPark(urlParkId);
          
          // Fetch calendar data directly
          const year = currentDate.getFullYear();
          const month = currentDate.getMonth() + 1;
          const calResponse = await adminAPI.getCalendarData(park.zone, park.division, urlParkId, year, month);
          setCalendarData(calResponse.data);
          
          toast.success(`Loaded ${park.park_name}`);
        }
      } catch (error) {
        console.error('Load park error:', error);
        toast.error('Failed to load park from URL');
      } finally {
        setLoading(false);
      }
    };
    
    loadParkFromUrl();
  }, [urlParkId, authLoading, user, userType, currentDate]);

  useEffect(() => {
    if (selectedZone) {
      fetchDivisions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone]);

  useEffect(() => {
    if (selectedZone && selectedDivision) {
      fetchParks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone, selectedDivision]);

  useEffect(() => {
    if (selectedPark) {
      fetchCalendarData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPark, currentDate]);

  // Elapsed time timers for downloads/uploads
  useEffect(() => {
    let interval;
    if (downloadingWorker) {
      interval = setInterval(() => {
        setWorkerDownloadElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setWorkerDownloadElapsed(0);
    }
    return () => clearInterval(interval);
  }, [downloadingWorker]);

  useEffect(() => {
    let interval;
    if (bulkDownloading) {
      interval = setInterval(() => {
        setBulkDownloadElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setBulkDownloadElapsed(0);
    }
    return () => clearInterval(interval);
  }, [bulkDownloading]);

  useEffect(() => {
    let interval;
    if (bulkUploading) {
      interval = setInterval(() => {
        setBulkUploadElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setBulkUploadElapsed(0);
    }
    return () => clearInterval(interval);
  }, [bulkUploading]);

  const fetchZones = async () => {
    try {
      const response = await adminAPI.getZones();
      setZones(sortNumeric(response.data.zones || []));
    } catch (error) {
      toast.error('Failed to load zones');
    }
  };

  const fetchSupervisors = async () => {
    try {
      const response = await adminAPI.getSupervisors();
      const supervisors = response.data.supervisors || response.data.inspectors || response.data;
      setSupervisorsList(Array.isArray(supervisors) ? supervisors : []);
    } catch (error) {
      console.error('Failed to load supervisors:', error);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await adminAPI.getDivisions(selectedZone);
      setDivisions(sortNumeric(response.data.divisions || []));
      setSelectedDivision('');
      setSelectedPark('');
    } catch (error) {
      toast.error('Failed to load divisions');
    }
  };

  const fetchParks = async () => {
    try {
      const response = await adminAPI.getParksList(selectedZone, selectedDivision);
      setParks(sortParksAlphabetically(response.data.parks || []));
      setSelectedPark('');
    } catch (error) {
      toast.error('Failed to load parks');
    }
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    setCalendarData(null);  // Clear previous data to avoid stale state
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await adminAPI.getCalendarData(selectedZone, selectedDivision, selectedPark, year, month);
      setCalendarData(response.data);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = async (dateData) => {
    // Check if this is a future date using local date comparison
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const clickedDateStr = dateData.date;
    
    if (clickedDateStr > today) {
      toast.info('Cannot add attendance for future dates');
      return;
    }
    
    // Use urlParkId if available (from redirect), otherwise use selectedPark
    const parkId = urlParkId || selectedPark;
    if (!parkId) {
      toast.error('No park selected');
      return;
    }

    if (!dateData.is_visited) {
      // For non-visited dates, open manual attendance modal
      setAddAttendanceDate(dateData.date);
      await fetchParkEmployees(dateData.date, parkId);
      setShowAddAttendanceModal(true);
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.getDateDetails(parkId, dateData.date);
      setDateDetails(response.data);
      setSelectedDate(dateData.date);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load date details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch park employees for manual attendance
  const fetchParkEmployees = async (targetDate, parkId = null) => {
    const effectiveParkId = parkId || urlParkId || selectedPark;
    if (!effectiveParkId) {
      toast.error('No park selected');
      return;
    }
    
    try {
      // Fetch park details directly from the API to get manpower list and supervisor
      const response = await adminAPI.getParkDetails(effectiveParkId);
      const parkData = response.data.park;
      
      // Fetch supervisor name if assigned
      if (parkData.assigned_inspector_id) {
        try {
          const supervisorResponse = await adminAPI.getSupervisorDetails(parkData.assigned_inspector_id);
          const supervisorData = supervisorResponse.data.supervisor;
          setParkSupervisor({
            id: supervisorData.supervisor_id,
            name: supervisorData.supervisor_name || supervisorData.supervisor_id
          });
        } catch (err) {
          // Fallback to just ID if supervisor fetch fails
          setParkSupervisor({
            id: parkData.assigned_inspector_id,
            name: parkData.assigned_inspector_id
          });
        }
      } else {
        setParkSupervisor(null);
      }
      
      if (parkData && parkData.manpower_list && parkData.manpower_list.length > 0) {
        setParkEmployees(parkData.manpower_list);
        
        // Check for existing attendance records for this date using the new endpoint
        try {
          const existingResponse = await adminAPI.getAttendanceByDate(effectiveParkId, targetDate);
          const existingAtt = existingResponse.data?.attendance || [];
          setExistingAttendance(existingAtt);
          
          // Initialize with existing status if available
          setManualAttendanceList(parkData.manpower_list.map(emp => {
            const existing = existingAtt.find(a => a.employee_id === emp.employee_id);
            return {
              ...emp,
              status: existing ? existing.status : ''
            };
          }));
        } catch (err) {
          // No existing attendance, initialize with empty status
          setExistingAttendance([]);
          setManualAttendanceList(parkData.manpower_list.map(emp => ({
            ...emp,
            status: ''
          })));
        }
      } else {
        // No employees found
        setParkEmployees([]);
        setManualAttendanceList([]);
        setExistingAttendance([]);
        toast.info('No employees assigned to this park. Please add employees in Data Management first.');
      }
    } catch (error) {
      console.error('Error fetching park employees:', error);
      toast.error('Failed to fetch park employees');
      setParkEmployees([]);
      setManualAttendanceList([]);
      setExistingAttendance([]);
      setParkSupervisor(null);
    }
  };

  // Handle manual attendance status change
  const handleManualAttendanceChange = (employeeId, status) => {
    setManualAttendanceList(prev => prev.map(emp => 
      emp.employee_id === employeeId ? { ...emp, status } : emp
    ));
  };

  // Save manual attendance for non-visited date
  const handleSaveManualAttendance = async () => {
    const markedAttendance = manualAttendanceList.filter(emp => emp.status);
    if (markedAttendance.length === 0) {
      toast.error('Please mark at least one employee attendance');
      return;
    }

    setAddingAttendance(true);
    try {
      const effectiveParkId = urlParkId || selectedPark;
      for (const emp of markedAttendance) {
        await adminAPI.editAttendance({
          date: addAttendanceDate,
          park_id: effectiveParkId,
          employee_id: emp.employee_id,
          status: emp.status,
          reason: 'Manual entry by admin (park not visited)'
        });
      }
      
      toast.success(`Attendance marked for ${markedAttendance.length} employee(s)`);
      setShowAddAttendanceModal(false);
      setManualAttendanceList([]);
      fetchCalendarData(); // Refresh calendar
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setAddingAttendance(false);
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Manual Attendance Edit Functions
  const handleEditClick = (attendance) => {
    setEditingAttendance(attendance);
    setEditStatus(attendance.status);
    setEditReason('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingAttendance || !editReason.trim()) {
      toast.error('Please provide a reason for the change');
      return;
    }

    setEditSaving(true);
    try {
      const effectiveParkId = urlParkId || selectedPark;
      await adminAPI.editAttendance({
        date: selectedDate,
        park_id: effectiveParkId,
        employee_id: editingAttendance.employee_id,
        status: editStatus,
        reason: editReason.trim()
      });
      
      toast.success(`Attendance updated for ${editingAttendance.employee_name}`);
      setShowEditModal(false);
      
      // Refresh the date details
      const response = await adminAPI.getDateDetails(effectiveParkId, selectedDate);
      setDateDetails(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setEditSaving(false);
    }
  };

  // Supervisor Attendance Calendar Functions
  const fetchSupervisorCalendar = async () => {
    setSupervisorLoading(true);
    try {
      const monthStr = `${supervisorMonth.getFullYear()}-${String(supervisorMonth.getMonth() + 1).padStart(2, '0')}`;
      const [calendarResponse, overtimeResponse] = await Promise.all([
        adminAPI.getSupervisorAttendanceCalendar(monthStr),
        adminAPI.getOvertimeAssignments(null, null, monthStr)
      ]);
      
      // Add overtime data to calendar
      const calendarData = calendarResponse.data;
      const overtimeAssignments = overtimeResponse.data.overtime_assignments || [];
      
      // Group overtime by OT supervisor (who does the work) and date
      const overtimeBySupDate = {};
      overtimeAssignments.forEach(ot => {
        // Use ot_supervisor_id (new schema) or supervisor_id (old schema) for backward compatibility
        const supId = ot.ot_supervisor_id || ot.supervisor_id;
        const key = `${supId}_${ot.date}`;
        if (!overtimeBySupDate[key]) overtimeBySupDate[key] = [];
        overtimeBySupDate[key].push(ot);
      });
      
      // Merge overtime info into supervisor data
      calendarData.supervisors = calendarData.supervisors?.map(sup => {
        const updatedAttendance = { ...sup.attendance };
        Object.keys(updatedAttendance).forEach(dateStr => {
          const key = `${sup.supervisor_id}_${dateStr}`;
          if (overtimeBySupDate[key]) {
            updatedAttendance[dateStr] = {
              ...updatedAttendance[dateStr],
              has_overtime: true,
              overtime_parks: overtimeBySupDate[key].length
            };
          }
        });
        
        // Also check for overtime on dates not in attendance
        Object.keys(overtimeBySupDate).forEach(key => {
          const [supId, dateStr] = key.split('_');
          if (supId === sup.supervisor_id && !updatedAttendance[dateStr]) {
            updatedAttendance[dateStr] = {
              status: '-',
              visits: 0,
              percentage: 0,
              color: 'gray',
              has_overtime: true,
              overtime_parks: overtimeBySupDate[key].length
            };
          }
        });
        
        return { ...sup, attendance: updatedAttendance };
      });
      
      setSupervisorCalendarData(calendarData);
    } catch (error) {
      toast.error('Failed to load supervisor attendance');
    } finally {
      setSupervisorLoading(false);
    }
  };

  // Manual Supervisor Attendance Functions
  const handleSupervisorCellClick = (supervisor, dateStr, dayData) => {
    // Don't allow editing future dates
    const today = new Date();
    const clickedDate = new Date(dateStr);
    if (clickedDate > today) {
      toast.error('Cannot mark attendance for future dates');
      return;
    }
    
    setSelectedSupervisorForAttendance(supervisor);
    setSelectedSupervisorDate(dateStr);
    setSupervisorAttendanceStatus(dayData.status === 'P' ? 'P' : dayData.status === 'A' ? 'A' : 'P');
    setSupervisorAttendanceRemarks('');
    setShowSupervisorAttendanceModal(true);
  };

  const handleSaveSupervisorAttendance = async () => {
    if (!selectedSupervisorForAttendance || !selectedSupervisorDate) {
      toast.error('Invalid selection');
      return;
    }

    setSavingSupervisorAttendance(true);
    try {
      await adminAPI.markSupervisorAttendanceManual({
        supervisor_id: selectedSupervisorForAttendance.supervisor_id,
        date: selectedSupervisorDate,
        status: supervisorAttendanceStatus,
        remarks: supervisorAttendanceRemarks.trim()
      });
      
      toast.success(`Attendance marked for ${selectedSupervisorForAttendance.supervisor_name}`);
      setShowSupervisorAttendanceModal(false);
      fetchSupervisorCalendar(); // Refresh calendar
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setSavingSupervisorAttendance(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'supervisor') {
      fetchSupervisorCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, supervisorMonth]);

  const handleDownloadSupervisorExcel = async () => {
    try {
      const monthStr = `${supervisorMonth.getFullYear()}-${String(supervisorMonth.getMonth() + 1).padStart(2, '0')}`;
      const response = await adminAPI.downloadSupervisorAttendanceExcel(monthStr);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = supervisorMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
      link.setAttribute('download', `Supervisor_Attendance_${monthName.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Excel');
    }
  };

  const handleDownloadWorkerExcel = async () => {
    setDownloadingWorker(true);
    setWorkerDownloadProgress(0);
    setBackgroundDownloadTaskId(null);
    setBackgroundDownloadStatus(null);
    
    // Simulate progress animation
    const progressInterval = setInterval(() => {
      setWorkerDownloadProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 10;
      });
    }, 500);
    
    try {
      const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const response = await adminAPI.downloadWorkerAttendanceExcel(monthStr, downloadZone, downloadSupervisor);
      
      clearInterval(progressInterval);
      setWorkerDownloadProgress(100);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
      const zoneLabel = downloadZone === 'all' ? 'All_Zones' : `Zone_${downloadZone}`;
      const supervisorLabel = downloadSupervisor === 'all' ? '' : `_Supervisor_${downloadSupervisor}`;
      link.setAttribute('download', `Worker_Attendance_${zoneLabel}${supervisorLabel}_${monthName.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Worker attendance Excel downloaded');
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Download error:', error);
      // Offer background download as fallback
      toast.error('Direct download timed out. Starting background download...');
      handleBackgroundWorkerDownload();
      return;
    } finally {
      setDownloadingWorker(false);
      setWorkerDownloadProgress(0);
    }
  };

  // Background download for worker attendance (fallback)
  const handleBackgroundWorkerDownload = async () => {
    try {
      const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const response = await adminAPI.startWorkerAttendanceBackgroundDownload(monthStr, downloadZone, downloadSupervisor);
      
      setBackgroundDownloadTaskId(response.data.task_id);
      setBackgroundDownloadStatus('processing');
      toast.info('Background download started. Please wait...');
      
      // Poll for status
      pollBackgroundDownload(response.data.task_id);
    } catch (error) {
      console.error('Background download error:', error);
      toast.error('Failed to start background download');
      setDownloadingWorker(false);
    }
  };

  // Poll background download status
  const pollBackgroundDownload = async (taskId) => {
    const checkStatus = async () => {
      try {
        const response = await adminAPI.checkDownloadStatus(taskId);
        setBackgroundDownloadStatus(response.data.status);
        setWorkerDownloadProgress(response.data.progress || 0);
        
        if (response.data.status === 'ready') {
          // Download the file
          const fileResponse = await adminAPI.downloadBackgroundFile(taskId);
          const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.data.filename || 'download.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success('Download complete!');
          setDownloadingWorker(false);
          setBackgroundDownloadTaskId(null);
          setBackgroundDownloadStatus(null);
          setWorkerDownloadProgress(0);
        } else if (response.data.status === 'failed') {
          toast.error(`Download failed: ${response.data.error}`);
          setDownloadingWorker(false);
          setBackgroundDownloadTaskId(null);
          setBackgroundDownloadStatus(null);
        } else {
          // Still processing, check again in 2 seconds
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Status check error:', error);
        toast.error('Failed to check download status');
        setDownloadingWorker(false);
      }
    };
    
    checkStatus();
  };

  // Format elapsed time helper
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleBulkDownload = async () => {
    if (!bulkEditStartDate || !bulkEditEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setBulkDownloading(true);
    setBulkDownloadProgress(0);
    setBulkUploadResult(null);
    
    const progressInterval = setInterval(() => {
      setBulkDownloadProgress(prev => prev >= 90 ? prev : prev + Math.random() * 10);
    }, 500);
    
    try {
      const response = await adminAPI.downloadBulkAttendanceTemplate(bulkEditStartDate, bulkEditEndDate, bulkEditZone);
      
      clearInterval(progressInterval);
      setBulkDownloadProgress(100);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const zoneLabel = bulkEditZone === 'all' ? 'All_Zones' : `Zone_${bulkEditZone}`;
      link.setAttribute('download', `Bulk_Attendance_Edit_${zoneLabel}_${bulkEditStartDate}_to_${bulkEditEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Bulk attendance template downloaded. Edit and re-upload.');
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Bulk download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download template');
    } finally {
      setBulkDownloading(false);
      setBulkDownloadProgress(0);
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setBulkUploading(true);
    setBulkUploadProgress(0);
    setBulkUploadResult(null);
    
    const progressInterval = setInterval(() => {
      setBulkUploadProgress(prev => prev >= 90 ? prev : prev + Math.random() * 8);
    }, 500);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await adminAPI.uploadBulkAttendance(formData);
      
      clearInterval(progressInterval);
      setBulkUploadProgress(100);
      
      setBulkUploadResult(response.data);
      
      if (response.data.updated_count > 0 || response.data.created_count > 0) {
        toast.success(`Updated: ${response.data.updated_count}, Created: ${response.data.created_count}`);
      } else {
        toast.info('No changes were made. Check if "New Status" column was filled.');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process upload');
    } finally {
      setBulkUploading(false);
      setBulkUploadProgress(0);
      event.target.value = ''; // Reset file input
    }
  };

  const getSupervisorColorClass = (color) => {
    const colors = {
      green: 'bg-green-500 text-white',
      yellow: 'bg-yellow-400 text-black',
      orange: 'bg-orange-500 text-white',
      red: 'bg-red-500 text-white',
      gray: 'bg-slate-200 text-slate-500'
    };
    return colors[color] || colors.gray;
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (viewMode === 'worker' && selectedPark) {
        await fetchCalendarData();
      } else if (viewMode === 'supervisor') {
        await fetchSupervisorCalendar();
      } else if (viewMode === 'corrections') {
        await fetchCorrections();
      } else if (viewMode === 'pending-worker') {
        await fetchPendingWorkerAttendance();
      } else {
        await fetchZones();
      }
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch corrections data
  const fetchCorrections = async () => {
    setCorrectionsLoading(true);
    try {
      const response = await adminAPI.getAttendanceCorrections(
        correctionsMonth,
        correctionsFilterDate || null,
        correctionsFilterSupervisor || null,
        correctionsFilterZone || null
      );
      let corrections = response.data.corrections || [];
      
      // Calculate counts from the filtered corrections data (after server-side filtering)
      const totalCount = corrections.length;
      const verifiedCount = corrections.filter(c => c.admin_verified).length;
      const unverifiedCount = corrections.filter(c => !c.admin_verified).length;
      
      setCorrectionsCounts({
        total: totalCount,
        verified: verifiedCount,
        unverified: unverifiedCount
      });
      
      // Apply verification filter on frontend
      if (correctionsFilterVerified === 'verified') {
        corrections = corrections.filter(c => c.admin_verified);
      } else if (correctionsFilterVerified === 'unverified') {
        corrections = corrections.filter(c => !c.admin_verified);
      }
      
      setCorrectionsData(corrections);
      setCorrectionsFilterOptions(response.data.filter_options || { dates: [], zones: [], supervisors: [] });
      
      // Default to collapsed view - collapse all dates
      const uniqueDates = [...new Set(corrections.map(c => c.date))];
      const allCollapsed = {};
      uniqueDates.forEach(d => allCollapsed[d] = true);
      setCollapsedDates(allCollapsed);
      
      // Also fetch rejected corrections with same filters INCLUDING month
      try {
        const rejectedRes = await adminAPI.getCancelledCorrections(
          correctionsFilterDate || null,
          correctionsFilterSupervisor || null,
          correctionsFilterZone || null,
          correctionsMonth  // Pass the month filter
        );
        setRejectedCorrections(rejectedRes.data.cancelled_corrections || []);
      } catch (e) {
        setRejectedCorrections([]);
      }
    } catch (error) {
      toast.error('Failed to load corrections data');
    } finally {
      setCorrectionsLoading(false);
    }
  };

  // Toggle verification status
  const handleVerifyToggle = async (correctionId, currentStatus) => {
    setVerifyingId(correctionId);
    try {
      await adminAPI.verifyCorrectionToggle(correctionId);
      
      // Update local state
      setCorrectionsData(prev => prev.map(c => 
        c.id === correctionId 
          ? { ...c, admin_verified: !currentStatus, verified_at: !currentStatus ? new Date().toISOString() : null }
          : c
      ));
      
      // Update counts
      setCorrectionsCounts(prev => ({
        ...prev,
        verified: prev.verified + (currentStatus ? -1 : 1),
        unverified: prev.unverified + (currentStatus ? 1 : -1)
      }));
      
      toast.success(currentStatus ? 'Acceptance removed' : 'Marked as accepted');
    } catch (error) {
      toast.error('Failed to update verification status');
    } finally {
      setVerifyingId(null);
    }
  };

  // Download corrections as Excel
  const handleDownloadCorrections = async () => {
    setDownloadingCorrections(true);
    const zoneLabel = correctionsFilterZone ? `Zone${correctionsFilterZone}` : 'AllZones';
    const filename = `Corrections_${correctionsMonth}_${zoneLabel}.xlsx`;
    
    await downloadFile(
      adminAPI.downloadAttendanceCorrections(
        correctionsMonth,
        correctionsFilterDate || null,
        correctionsFilterSupervisor || null,
        correctionsFilterZone || null
      ),
      filename,
      () => toast.success('Corrections downloaded'),
      (error) => toast.error(error || 'Failed to download corrections')
    );
    setDownloadingCorrections(false);
  };

  // Fetch corrections when view mode changes to corrections or filters change
  useEffect(() => {
    if (viewMode === 'corrections') {
      fetchCorrections();
    }
  }, [viewMode, correctionsMonth, correctionsFilterDate, correctionsFilterSupervisor, correctionsFilterZone, correctionsFilterVerified]);

  const fetchPendingWorkerAttendance = async () => {
    if (!selectedSupervisor) {
      setPendingWorkerData(null);
      return;
    }
    setPendingWorkerLoading(true);
    try {
      const response = await adminAPI.getPendingWorkerAttendance(selectedSupervisor, pendingWorkerMonth);
      setPendingWorkerData(response.data);
      
      // Auto-expand first 3 parks
      const autoExpand = {};
      (response.data.parks || []).slice(0, 3).forEach(p => {
        autoExpand[p.park_id] = true;
      });
      setPendingWorkerExpandedParks(autoExpand);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load pending worker attendance');
      setPendingWorkerData(null);
    } finally {
      setPendingWorkerLoading(false);
    }
  };

  const changePendingWorkerMonth = (delta) => {
    const [year, month] = pendingWorkerMonth.split('-').map(Number);
    let newMonth = month + delta;
    let newYear = year;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMon = now.getMonth() + 1;
    
    if (newYear > currentYear || (newYear === currentYear && newMonth > currentMon)) {
      toast.info('Cannot view future months');
      return;
    }
    
    setPendingWorkerMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const togglePendingWorkerPark = (parkId) => {
    setPendingWorkerExpandedParks(prev => ({
      ...prev,
      [parkId]: !prev[parkId]
    }));
  };

  useEffect(() => {
    if (viewMode === 'pending-worker') {
      fetchPendingWorkerAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedSupervisor, pendingWorkerMonth]);

  const supervisorMonthName = supervisorMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const pendingWorkerMonthName = new Date(pendingWorkerMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });

  const getColorClass = (colorCode) => {
    const colors = {
      green: 'bg-green-100 border-green-500 hover:bg-green-200',
      yellow: 'bg-yellow-100 border-yellow-500 hover:bg-yellow-200',
      red: 'bg-red-100 border-red-500 hover:bg-red-200',
      blue: 'bg-blue-100 border-blue-500 hover:bg-blue-200',
      grey: 'bg-slate-100 border-slate-300 hover:bg-slate-200',
      purple: 'bg-purple-100 border-purple-500 hover:bg-purple-200'
    };
    return colors[colorCode] || colors.grey;
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:opacity-80">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Attendance Calendar
              </h1>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-calendar-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* View Mode Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setViewMode('worker')}
            className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors ${
              viewMode === 'worker' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-blue-600'
            }`}
            data-testid="worker-view-tab"
          >
            <Users className="w-5 h-5" />
            Worker Attendance
          </button>
          <button
            onClick={() => setViewMode('supervisor')}
            className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors ${
              viewMode === 'supervisor' 
                ? 'bg-purple-600 text-white' 
                : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-purple-600'
            }`}
            data-testid="supervisor-view-tab"
          >
            <CalendarIcon className="w-5 h-5" />
            Supervisor Attendance
          </button>
          <button
            onClick={() => setViewMode('corrections')}
            className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors ${
              viewMode === 'corrections' 
                ? 'bg-orange-600 text-white' 
                : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-orange-600'
            }`}
            data-testid="corrections-view-tab"
          >
            <UserCheck className="w-5 h-5" />
            Supervisor Corrections
          </button>
          <button
            onClick={() => setViewMode('pending-worker')}
            className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors ${
              viewMode === 'pending-worker' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-amber-600'
            }`}
            data-testid="pending-worker-view-tab"
          >
            <AlertCircle className="w-5 h-5" />
            Pending Worker Attendance
          </button>
        </div>

        {/* Worker Attendance View */}
        {viewMode === 'worker' && (
          <>
            {/* Worker Download Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">Download Worker Attendance</h3>
                  <p className="text-sm text-slate-500">Download monthly attendance with P/A for all workers</p>
                </div>
                <select
                  value={downloadZone}
                  onChange={(e) => setDownloadZone(e.target.value)}
                  className="h-10 px-4 rounded-lg border-2 border-slate-300"
                  disabled={downloadingWorker}
                >
                  <option value="all">All Zones</option>
                  {zones.map(z => <option key={z} value={z}>Zone {z}</option>)}
                </select>
                <select
                  value={downloadSupervisor}
                  onChange={(e) => setDownloadSupervisor(e.target.value)}
                  className="h-10 px-4 rounded-lg border-2 border-slate-300"
                  disabled={downloadingWorker}
                >
                  <option value="all">All Supervisors</option>
                  {supervisorsList.map(s => (
                    <option key={s.supervisor_id} value={s.supervisor_id}>
                      {s.supervisor_name} ({s.supervisor_id})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleDownloadWorkerExcel}
                  disabled={downloadingWorker}
                  className="h-10 px-6 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
                  data-testid="download-worker-excel-btn"
                >
                  {downloadingWorker ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {downloadingWorker ? 'Generating...' : 'Download Excel'}
                </button>
              </div>
              
              {/* Download Progress Bar */}
              {downloadingWorker && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm font-semibold text-blue-800">
                        {backgroundDownloadStatus === 'processing' 
                          ? 'Background processing... This is more reliable for large data'
                          : 'Generating report... Please wait'}
                      </span>
                    </div>
                    <span className="text-sm text-blue-600">
                      {backgroundDownloadStatus ? `${Math.round(workerDownloadProgress)}%` : formatElapsedTime(workerDownloadElapsed) + ' elapsed'}
                    </span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(workerDownloadProgress, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    {backgroundDownloadStatus 
                      ? 'Processing in background. File will download automatically when ready.'
                      : 'Large datasets may take 1-2 minutes. Do not close this page.'}
                  </p>
                </div>
              )}
            </div>

            {/* Bulk Edit Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border-l-4 border-orange-500">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowBulkEditSection(!showBulkEditSection)}
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-6 h-6 text-orange-600" />
                  <div>
                    <h3 className="font-bold text-lg">Bulk Edit Attendance</h3>
                    <p className="text-sm text-slate-500">Download, edit in Excel, and re-upload to update multiple records</p>
                  </div>
                </div>
                <ChevronRight className={`w-6 h-6 text-slate-400 transition-transform ${showBulkEditSection ? 'rotate-90' : ''}`} />
              </div>
              
              {showBulkEditSection && (
                <div className="mt-4 pt-4 border-t">
                  {/* Date Range & Zone Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-1 block">Start Date</label>
                      <input
                        type="date"
                        value={bulkEditStartDate}
                        onChange={(e) => setBulkEditStartDate(e.target.value)}
                        className="h-10 px-3 w-full rounded-lg border-2 border-slate-300 focus:border-orange-500 focus:ring-0"
                        data-testid="bulk-start-date"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-1 block">End Date</label>
                      <input
                        type="date"
                        value={bulkEditEndDate}
                        onChange={(e) => setBulkEditEndDate(e.target.value)}
                        className="h-10 px-3 w-full rounded-lg border-2 border-slate-300 focus:border-orange-500 focus:ring-0"
                        data-testid="bulk-end-date"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-1 block">Zone</label>
                      <select
                        value={bulkEditZone}
                        onChange={(e) => setBulkEditZone(e.target.value)}
                        className="h-10 px-3 w-full rounded-lg border-2 border-slate-300 focus:border-orange-500 focus:ring-0"
                        data-testid="bulk-zone-select"
                      >
                        <option value="all">All Zones</option>
                        {zones.map(z => <option key={z} value={z}>Zone {z}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <button
                        onClick={handleBulkDownload}
                        disabled={bulkDownloading || !bulkEditStartDate || !bulkEditEndDate}
                        className="h-10 px-4 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                        data-testid="bulk-download-btn"
                      >
                        {bulkDownloading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Download
                      </button>
                      <label className={`h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center gap-2 cursor-pointer ${bulkUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                        {bulkUploading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Upload
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleBulkUpload}
                          className="hidden"
                          disabled={bulkUploading}
                          data-testid="bulk-upload-input"
                        />
                      </label>
                    </div>
                  </div>
                  
                  {/* Download Progress */}
                  {bulkDownloading && (
                    <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm font-semibold text-orange-800">Generating template...</span>
                        </div>
                        <span className="text-sm text-orange-600">{formatElapsedTime(bulkDownloadElapsed)} elapsed</span>
                      </div>
                      <div className="w-full bg-orange-100 rounded-full h-3">
                        <div className="bg-orange-600 h-3 rounded-full transition-all duration-300" style={{ width: `${Math.min(bulkDownloadProgress, 100)}%` }} />
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Progress */}
                  {bulkUploading && (
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm font-semibold text-blue-800">Processing upload...</span>
                        </div>
                        <span className="text-sm text-blue-600">{formatElapsedTime(bulkUploadElapsed)} elapsed</span>
                      </div>
                      <div className="w-full bg-blue-100 rounded-full h-3">
                        <div className="bg-blue-600 h-3 rounded-full transition-all duration-300" style={{ width: `${Math.min(bulkUploadProgress, 100)}%` }} />
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Result */}
                  {bulkUploadResult && (
                    <div className={`p-4 rounded-lg ${bulkUploadResult.total_errors > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {bulkUploadResult.total_errors > 0 ? (
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        )}
                        <span className="font-bold">{bulkUploadResult.message}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="bg-white p-2 rounded text-center">
                          <div className="text-xl font-bold text-green-600">{bulkUploadResult.updated_count}</div>
                          <div className="text-slate-500">Updated</div>
                        </div>
                        <div className="bg-white p-2 rounded text-center">
                          <div className="text-xl font-bold text-blue-600">{bulkUploadResult.created_count}</div>
                          <div className="text-slate-500">Created</div>
                        </div>
                        <div className="bg-white p-2 rounded text-center">
                          <div className="text-xl font-bold text-slate-400">{bulkUploadResult.skipped_count}</div>
                          <div className="text-slate-500">Skipped</div>
                        </div>
                      </div>
                      {bulkUploadResult.errors?.length > 0 && (
                        <div className="mt-3 text-sm text-red-600">
                          <div className="font-bold">Errors ({bulkUploadResult.total_errors}):</div>
                          <ul className="list-disc pl-5 mt-1 max-h-32 overflow-y-auto">
                            {bulkUploadResult.errors.map((err, idx) => <li key={idx}>{err}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Instructions */}
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                    <strong>Instructions:</strong>
                    <ol className="list-decimal pl-5 mt-1 space-y-1">
                      <li>Select date range and zone, then click <strong>Download</strong></li>
                      <li>Open the Excel file and fill the <strong>"New Status"</strong> column with P (Present) or A (Absent)</li>
                      <li>Leave "New Status" empty for records you don't want to change</li>
                      <li>Save the file and click <strong>Upload</strong> to apply changes</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 mb-2 block">Zone</label>
                  <select
                    value={selectedZone}
                    onChange={(e) => setSelectedZone(e.target.value)}
                    className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                    data-testid="zone-select"
                  >
                    <option value="">Select Zone</option>
                    {zones.map((zone) => (
                      <option key={zone} value={zone}>{zone}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 mb-2 block">Division</label>
                  <select
                    value={selectedDivision}
                    onChange={(e) => setSelectedDivision(e.target.value)}
                    disabled={!selectedZone}
                    className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 disabled:opacity-50"
                    data-testid="division-select"
                  >
                    <option value="">Select Division</option>
                    {divisions.map((division) => (
                      <option key={division} value={division}>{division}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 mb-2 block">Park</label>
                  <select
                    value={selectedPark}
                    onChange={(e) => setSelectedPark(e.target.value)}
                    disabled={!selectedDivision}
                    className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 disabled:opacity-50"
                    data-testid="park-select"
                  >
                    <option value="">Select Park</option>
                    {parks.map((park) => (
                      <option key={park.park_id} value={park.park_id}>{park.park_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 mb-2 block">Month</label>
                  <div className="flex items-center gap-2">
                <button
                  onClick={() => changeMonth(-1)}
                  className="h-12 w-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 h-12 px-4 rounded-lg border-2 border-slate-300 flex items-center justify-center font-semibold">
                  {monthName}
                </div>
                <button
                  onClick={() => changeMonth(1)}
                  className="h-12 w-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        {selectedPark && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <h3 className="font-bold mb-3">Legend</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 border-2 border-green-500 rounded"></div>
                <span>Full Attendance</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-yellow-100 border-2 border-yellow-500 rounded"></div>
                <span>Partial Attendance</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-100 border-2 border-red-500 rounded"></div>
                <span>No Manpower</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 border-2 border-blue-500 rounded"></div>
                <span>Closed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-slate-100 border-2 border-slate-300 rounded"></div>
                <span>Not Visited</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-yellow-200 border-2 border-yellow-500 rounded"></div>
                <span>Under Renovation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-red-600 font-semibold">Temp Worker</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-purple-600 font-semibold">Manual Entry</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-orange-600 font-semibold">Closed Park Edited</span>
              </div>
            </div>
          </div>
        )}

        {/* Calendar */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-slate-600">Loading calendar...</p>
          </div>
        ) : calendarData ? (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{calendarData.park.park_name}</h2>
              {calendarData.park.is_active === false && (
                <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  UNDER RENOVATION
                </span>
              )}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center font-bold text-slate-700 py-2">
                  {day}
                </div>
              ))}
              
              {/* Empty cells for alignment */}
              {Array.from({ length: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() }).map((_, idx) => (
                <div key={`empty-${idx}`} className="h-24"></div>
              ))}
              
              {/* Date cells */}
              {calendarData.calendar_data.map((dateData) => {
                // Check if this is a past date (can add manual attendance)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const cellDate = new Date(dateData.date);
                const isPastDate = cellDate <= today;
                const isClickable = dateData.is_visited || isPastDate;
                
                return (
                  <button
                    key={dateData.date}
                    onClick={() => handleDateClick(dateData)}
                    disabled={!isClickable}
                    className={`h-24 border-2 rounded-lg p-2 text-left transition-all ${getColorClass(dateData.color_code)} ${
                      isClickable ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed opacity-50'
                    } ${dateData.has_manual_edit ? 'ring-2 ring-purple-500' : ''} ${dateData.has_closed_edit ? 'ring-2 ring-orange-500' : ''}`}
                    data-testid={`calendar-date-${dateData.day}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-lg">{dateData.day}</span>
                      <div className="flex items-center gap-1">
                        {dateData.has_manual_edit && (
                          <span className="w-3 h-3 bg-purple-500 rounded-full" title="Manual Entry"></span>
                        )}
                        {dateData.has_closed_edit && (
                          <span className="w-3 h-3 bg-orange-500 rounded-full" title="Edited (Closed Park)"></span>
                        )}
                        {dateData.has_temp_workers && (
                          <span className="w-3 h-3 bg-red-500 rounded-full" title={`${dateData.temp_worker_count} Temp Worker(s)`}></span>
                        )}
                      </div>
                    </div>
                    {dateData.is_visited ? (
                      <>
                        <div className="text-xs font-semibold flex items-center gap-1">
                          {dateData.park_status}
                          {dateData.has_closed_edit && <span className="text-orange-600">(E)</span>}
                        </div>
                        {dateData.park_status === 'Open' && (
                          <div className="text-xs mt-1 flex justify-between">
                            <div>
                              <div>P: {dateData.present_count}</div>
                              <div>A: {dateData.absent_count}</div>
                            </div>
                            {dateData.has_temp_workers && (
                              <div className="text-red-600 font-bold">T: {dateData.temp_worker_count}</div>
                            )}
                          </div>
                        )}
                        {dateData.park_status === 'Closed' && dateData.has_closed_edit && (
                          <div className="text-xs mt-1 text-orange-600 font-semibold">
                            Edited
                          </div>
                        )}
                      </>
                    ) : dateData.has_manual_edit ? (
                      <div className="text-xs">
                        <div className="text-purple-600 font-semibold">MANUAL</div>
                        <div>P: {dateData.present_count || 0}</div>
                        <div>A: {dateData.absent_count || 0}</div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500">
                        {isPastDate ? 'Click to add' : 'NOT VISITED'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-600">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <p>Select Zone, Division, and Park to view calendar</p>
          </div>
        )}

      {/* Detail Modal */}
      {showModal && dateDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Inspection Details - {selectedDate}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Park Details */}
              <div>
                <h3 className="font-bold text-lg mb-3">Park Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Park:</span> {dateDetails.park.park_name}
                  </div>
                  <div>
                    <span className="font-semibold">Zone:</span> {dateDetails.park.zone}
                  </div>
                  <div>
                    <span className="font-semibold">Division:</span> {dateDetails.park.division}
                  </div>
                  <div>
                    <span className="font-semibold">Inspector:</span> {dateDetails.inspector.supervisor_name}
                  </div>
                  <div>
                    <span className="font-semibold">Start Time:</span> {new Date(dateDetails.inspection.start_time).toLocaleTimeString()}
                  </div>
                  <div>
                    <span className="font-semibold">End Time:</span> {new Date(dateDetails.inspection.end_time).toLocaleTimeString()}
                  </div>
                  <div>
                    <span className="font-semibold">Duration:</span> {dateDetails.inspection.duration_minutes} minutes
                  </div>
                  <div>
                    <span className="font-semibold">Status:</span>{' '}
                    <span className={`px-2 py-1 rounded font-bold text-xs ${
                      dateDetails.inspection.park_status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {dateDetails.inspection.park_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Attendance - Show for both Open and Closed parks */}
              {(dateDetails.inspection.park_status === 'Open' || (dateDetails.attendance && dateDetails.attendance.length > 0)) ? (
                <>
                  <div>
                    <h3 className="font-bold text-lg mb-3">
                      Attendance Summary
                      {dateDetails.inspection.park_status === 'Closed' && (
                        <span className="ml-2 text-sm font-normal text-red-600">(Park was Closed)</span>
                      )}
                    </h3>
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-slate-100 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold">{dateDetails.summary.required_manpower}</div>
                        <div className="text-sm text-slate-600">Required</div>
                      </div>
                      <div className="bg-green-100 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{dateDetails.summary.present_count}</div>
                        <div className="text-sm text-green-700">Present</div>
                      </div>
                      <div className="bg-red-100 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">{dateDetails.summary.absent_count}</div>
                        <div className="text-sm text-red-700">Absent</div>
                      </div>
                      <div className="bg-orange-100 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-700">{dateDetails.summary.temp_worker_count || 0}</div>
                        <div className="text-sm text-orange-700">Temp Workers</div>
                      </div>
                    </div>

                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-3 font-bold">Employee Name</th>
                          <th className="text-left p-3 font-bold">Employee ID</th>
                          <th className="text-center p-3 font-bold">Status</th>
                          <th className="text-center p-3 font-bold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dateDetails.attendance.map((att, idx) => (
                          <tr key={idx} className={`border-t ${att.is_deleted && !att.is_temporary ? 'bg-slate-100' : att.is_temporary ? 'bg-red-50' : ''}`}>
                            <td className={`p-3 ${att.is_deleted && !att.is_temporary ? 'text-slate-500 italic' : att.is_temporary ? 'text-red-700' : ''}`}>
                              <div className="flex items-center gap-2">
                                {att.is_temporary && <span className="w-2 h-2 bg-red-500 rounded-full" title="Temporary Worker"></span>}
                                {att.employee_name}
                                {att.is_deleted && !att.is_temporary && <span className="ml-1 text-xs text-slate-500">(Removed)</span>}
                                {att.is_temporary && <span className="ml-1 text-xs text-red-500 font-bold">(Temporary)</span>}
                                {att.manually_edited && <span className="ml-1 text-xs text-blue-500">(Edited)</span>}
                              </div>
                              {att.edit_reason && (
                                <div className="text-xs text-blue-600 mt-1">Reason: {att.edit_reason}</div>
                              )}
                            </td>
                            <td className={`p-3 ${att.is_deleted && !att.is_temporary ? 'text-slate-400 italic' : att.is_temporary ? 'text-red-600' : ''}`}>{att.employee_id}</td>
                            <td className="p-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                att.status === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {att.status}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => handleEditClick(att)}
                                className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                                title="Edit Attendance"
                                data-testid={`edit-attendance-${att.employee_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Temporary Workers */}
                  {dateDetails.temporary_workers && dateDetails.temporary_workers.length > 0 && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                      <h3 className="font-bold text-lg mb-3 text-red-700">Temporary Workers</h3>
                      <table className="w-full text-sm">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="text-left p-3 font-bold text-red-700">Worker Name</th>
                            <th className="text-left p-3 font-bold text-red-700">Reason</th>
                            <th className="text-center p-3 font-bold text-red-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dateDetails.temporary_workers.map((tw, idx) => (
                            <tr key={idx} className="border-t border-red-200">
                              <td className="p-3 font-medium text-red-700">{tw.name} <span className="text-xs">(Temporary)</span></td>
                              <td className="p-3 text-red-600">{tw.reason || '-'}</td>
                              <td className="p-3 text-center">
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                                  Present
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-bold mb-2">Tools Available</h3>
                      <span className={`px-4 py-2 rounded-lg font-bold ${
                        dateDetails.inspection.tools_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {dateDetails.inspection.tools_available ? 'YES' : 'NO'}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Cleanliness</h3>
                      <span className={`px-4 py-2 rounded-lg font-bold ${
                        dateDetails.inspection.cleanliness === 'Good'
                          ? 'bg-green-100 text-green-700'
                          : dateDetails.inspection.cleanliness === 'Average'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {dateDetails.inspection.cleanliness}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    {dateDetails.inspection.workers_photo_url && (
                      <div>
                        <h3 className="font-bold mb-2">Workers Photo</h3>
                        <img
                          src={dateDetails.inspection.workers_photo_url}
                          alt="Workers"
                          className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90"
                          onClick={() => window.open(dateDetails.inspection.workers_photo_url, '_blank')}
                        />
                      </div>
                    )}
                    {dateDetails.inspection.cleanliness_photo_url && (
                      <div>
                        <h3 className="font-bold mb-2">Cleanliness Photo</h3>
                        <img
                          src={dateDetails.inspection.cleanliness_photo_url}
                          alt="Cleanliness"
                          className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90"
                          onClick={() => window.open(dateDetails.inspection.cleanliness_photo_url, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : dateDetails.inspection.park_status === 'Closed' ? (
                // Closed park with no attendance - show option to add manual attendance
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 text-center">
                  <h3 className="font-bold text-lg mb-2 text-orange-700">Park was Closed</h3>
                  <p className="text-sm text-orange-600 mb-4">No attendance was recorded for this closed park.</p>
                  <button
                    onClick={async () => {
                      setAddAttendanceDate(selectedDate);
                      await fetchParkEmployees();
                      setShowAddAttendanceModal(true);
                    }}
                    className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold flex items-center gap-2 mx-auto"
                    data-testid="add-closed-park-attendance"
                  >
                    <Plus className="w-5 h-5" />
                    Add Manual Attendance
                  </button>
                </div>
              ) : null}

              {dateDetails.inspection.park_status === 'Closed' && dateDetails.inspection.closed_photo_url && (
                <div>
                  <h3 className="font-bold mb-2">Closed Park Photo</h3>
                  <img
                    src={dateDetails.inspection.closed_photo_url}
                    alt="Closed park"
                    className="w-full max-w-md h-64 object-cover rounded-lg cursor-pointer hover:opacity-90"
                    onClick={() => window.open(dateDetails.inspection.closed_photo_url, '_blank')}
                  />
                </div>
              )}

              {dateDetails.inspection.remarks && (
                <div>
                  <h3 className="font-bold mb-2">Remarks</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">{dateDetails.inspection.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Edit Modal */}
      {showEditModal && editingAttendance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="bg-blue-600 text-white p-4 rounded-t-xl">
              <h2 className="text-lg font-bold">Edit Attendance</h2>
              <p className="text-sm opacity-90">{editingAttendance.employee_name}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Employee</label>
                <div className="bg-slate-100 p-3 rounded-lg">
                  <div className="font-semibold">{editingAttendance.employee_name}</div>
                  <div className="text-sm text-slate-500">ID: {editingAttendance.employee_id}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Current Status</label>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  editingAttendance.status === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {editingAttendance.status}
                </span>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">New Status *</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditStatus('Present')}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm transition-colors ${
                      editStatus === 'Present' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid="status-present-btn"
                  >
                    Present
                  </button>
                  <button
                    onClick={() => setEditStatus('Absent')}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm transition-colors ${
                      editStatus === 'Absent' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid="status-absent-btn"
                  >
                    Absent
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Reason for Change *</label>
                <textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Enter reason for manual edit (required for audit)"
                  className="w-full h-24 px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:ring-0 resize-none"
                  data-testid="edit-reason-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 py-3 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-50"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving || !editReason.trim() || editStatus === editingAttendance.status}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
                  data-testid="save-edit-btn"
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Add Attendance Modal (for non-visited dates) */}
      {showAddAttendanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl max-h-[90vh] overflow-hidden">
            <div className="bg-purple-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Manual Attendance Entry
                </h2>
                <p className="text-sm opacity-90">
                  {existingAttendance.length > 0 ? 'Edit attendance for' : 'Park was not visited on'} {addAttendanceDate}
                </p>
                {parkSupervisor && (
                  <p className="text-sm opacity-75 mt-1">
                    Supervisor: {parkSupervisor.name} ({parkSupervisor.id})
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowAddAttendanceModal(false);
                  setManualAttendanceList([]);
                  setParkSupervisor(null);
                  setExistingAttendance([]);
                }}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {parkEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No employees assigned to this park</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600 mb-4">
                    {existingAttendance.length > 0 
                      ? 'Update attendance for employees. Changes will be saved to the audit log.'
                      : 'Mark attendance for employees. Click Present or Absent for each employee.'}
                  </p>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left p-3 font-bold">Employee Name</th>
                        <th className="text-left p-3 font-bold">Employee ID</th>
                        <th className="text-left p-3 font-bold">Category</th>
                        <th className="text-center p-3 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualAttendanceList.map((emp, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3 font-medium">{emp.employee_name}</td>
                          <td className="p-3 text-slate-600">{emp.employee_id}</td>
                          <td className="p-3 text-slate-600">{emp.category || '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => handleManualAttendanceChange(emp.employee_id, 'Present')}
                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                                  emp.status === 'Present'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-green-100'
                                }`}
                              >
                                Present
                              </button>
                              <button
                                onClick={() => handleManualAttendanceChange(emp.employee_id, 'Absent')}
                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                                  emp.status === 'Absent'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-red-100'
                                }`}
                              >
                                Absent
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>

            <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {manualAttendanceList.filter(e => e.status).length} of {manualAttendanceList.length} marked
                {existingAttendance.length > 0 && (
                  <span className="ml-2 text-purple-600">(Editing existing records)</span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddAttendanceModal(false);
                    setManualAttendanceList([]);
                    setParkSupervisor(null);
                    setExistingAttendance([]);
                  }}
                  className="px-6 py-2 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveManualAttendance}
                  disabled={addingAttendance || manualAttendanceList.filter(e => e.status).length === 0}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
                >
                  {addingAttendance ? 'Saving...' : (existingAttendance.length > 0 ? 'Update Attendance' : 'Save Attendance')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          </>
        )}

        {/* Supervisor Attendance View */}
        {viewMode === 'supervisor' && (
          <div className="space-y-6">
            {/* Header with Download */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold">Supervisor Attendance Calendar</h2>
                  <p className="text-sm text-slate-500">Based on completed park visits (not just login)</p>
                </div>
                <button
                  onClick={handleDownloadSupervisorExcel}
                  className="h-10 px-6 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center gap-2"
                  data-testid="download-supervisor-excel-btn"
                >
                  <Download className="w-4 h-4" />
                  Download Excel
                </button>
              </div>
            </div>

            {/* Month Navigation */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setSupervisorMonth(new Date(supervisorMonth.getFullYear(), supervisorMonth.getMonth() - 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <h3 className="text-xl font-bold min-w-[200px] text-center">{supervisorMonthName}</h3>
                <button
                  onClick={() => setSupervisorMonth(new Date(supervisorMonth.getFullYear(), supervisorMonth.getMonth() + 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Color Legend */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h4 className="font-bold mb-3">Color Legend (% of Assigned Parks Visited)</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-red-500"></span>
                  <span>&lt; 25%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-orange-500"></span>
                  <span>25-50%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-yellow-400"></span>
                  <span>50-75%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-green-500"></span>
                  <span>&gt; 75%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-slate-200"></span>
                  <span>Absent (No visits)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-purple-500 text-white text-xs flex items-center justify-center font-bold">H</span>
                  <span>Holiday (Sunday)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-amber-400 text-white text-xs flex items-center justify-center font-bold">L</span>
                  <span>Leave (Manual)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <span className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center"></span>
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 text-white rounded-full text-[6px] font-bold flex items-center justify-center">OT</span>
                  </div>
                  <span>Overtime Assigned</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">Click on any cell to manually mark attendance</p>
            </div>

            {/* Supervisor Calendar Table */}
            {supervisorLoading ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600">Loading supervisor attendance...</p>
              </div>
            ) : supervisorCalendarData ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3 font-bold sticky left-0 bg-slate-100 min-w-[150px]">Supervisor</th>
                        <th className="text-center p-2 font-bold min-w-[60px]">Assigned</th>
                        {Array.from({ length: supervisorCalendarData.days_in_month }, (_, i) => (
                          <th key={i + 1} className="text-center p-2 font-bold min-w-[40px]">{i + 1}</th>
                        ))}
                        <th className="text-center p-2 font-bold bg-green-100 min-w-[50px]">P</th>
                        <th className="text-center p-2 font-bold bg-red-100 min-w-[50px]">A</th>
                        <th className="text-center p-2 font-bold bg-orange-100 min-w-[50px]">OT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supervisorCalendarData.supervisors.map((sup, idx) => (
                        <tr key={sup.supervisor_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-3 font-semibold sticky left-0 bg-inherit border-r">
                            <div>{sup.supervisor_name}</div>
                            <div className="text-xs text-slate-500">{sup.supervisor_id}</div>
                          </td>
                          <td className="text-center p-2 border-r font-bold">{sup.assigned_parks}</td>
                          {Array.from({ length: supervisorCalendarData.days_in_month }, (_, i) => {
                            const day = i + 1;
                            const dateStr = `${supervisorMonth.getFullYear()}-${String(supervisorMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            const dayData = sup.attendance[dateStr] || { status: 'A', color: 'gray' };
                            const isHoliday = dayData.is_holiday || dayData.status === 'H';
                            const isLeave = dayData.status === 'L';
                            const isManual = dayData.is_manual;
                            const hasOvertime = dayData.has_overtime;
                            const isFuture = new Date(dateStr) > new Date();
                            return (
                              <td key={day} className="text-center p-1">
                                <div className="relative">
                                  <button 
                                    onClick={() => !isFuture && handleSupervisorCellClick(sup, dateStr, dayData)}
                                    disabled={isFuture}
                                    className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold mx-auto transition-transform ${
                                      isHoliday ? 'bg-purple-500 text-white' : 
                                      isLeave ? 'bg-amber-400 text-white' :
                                      getSupervisorColorClass(dayData.color)
                                    } ${isManual ? 'ring-2 ring-blue-400' : ''} ${!isFuture ? 'hover:scale-110 hover:ring-2 hover:ring-blue-400 cursor-pointer' : 'cursor-default'}`}
                                    title={isHoliday ? 'Holiday (Sunday)' : isLeave ? `Leave${dayData.remarks ? ': ' + dayData.remarks : ''}` : isFuture ? 'Future date' : dayData.visits > 0 ? `Visits: ${dayData.visits} (${dayData.percentage}%)${isManual ? ' - Manual' : ''}${hasOvertime ? ' - OVERTIME' : ''} - Click to edit` : `No visits${hasOvertime ? ' - OVERTIME' : ''} - Click to mark`}
                                  >
                                    {isHoliday ? 'H' : isLeave ? 'L' : dayData.status}
                                  </button>
                                  {/* Overtime indicator */}
                                  {hasOvertime && (
                                    <span 
                                      className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white rounded-full text-[8px] font-bold flex items-center justify-center"
                                      title={`${dayData.overtime_parks} overtime park(s)`}
                                    >
                                      OT
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className="text-center p-2 font-bold text-green-700 bg-green-50">{sup.total_present}</td>
                          <td className="text-center p-2 font-bold text-red-700 bg-red-50">{sup.total_absent}</td>
                          <td className="text-center p-2 font-bold text-orange-700 bg-orange-50">
                            {Object.values(sup.attendance || {}).filter(d => d.has_overtime).length || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-600">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <p>No supervisor data available</p>
              </div>
            )}
          </div>
        )}

        {/* Supervisor Corrections View */}
        {viewMode === 'corrections' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold">Supervisor Attendance Corrections</h2>
                  <p className="text-sm text-slate-500">Workers marked present by supervisors with photo proof</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-600">
                    {correctionsCounts.total} total
                  </span>
                  <span className="text-sm font-semibold text-green-600">
                    {correctionsCounts.verified} accepted
                  </span>
                  {correctionsCounts.unverified > 0 && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold">
                      {correctionsCounts.unverified} pending
                    </span>
                  )}
                  {rejectedCorrections.length > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold">
                      {rejectedCorrections.length} rejected
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Month</label>
                  <input
                    type="month"
                    value={correctionsMonth}
                    onChange={(e) => {
                      setCorrectionsMonth(e.target.value);
                      setCorrectionsFilterDate('');
                    }}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Date</label>
                  <select
                    value={correctionsFilterDate}
                    onChange={(e) => setCorrectionsFilterDate(e.target.value)}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  >
                    <option value="">All Dates</option>
                    {correctionsFilterOptions.dates.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Supervisor</label>
                  <select
                    value={correctionsFilterSupervisor}
                    onChange={(e) => setCorrectionsFilterSupervisor(e.target.value)}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  >
                    <option value="">All Supervisors</option>
                    {correctionsFilterOptions.supervisors.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.id})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Zone</label>
                  <select
                    value={correctionsFilterZone}
                    onChange={(e) => setCorrectionsFilterZone(e.target.value)}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  >
                    <option value="">All Zones</option>
                    {(correctionsFilterOptions.zones || []).map(z => (
                      <option key={z} value={z}>Zone {z}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
                  <select
                    value={correctionsFilterVerified}
                    onChange={(e) => setCorrectionsFilterVerified(e.target.value)}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  >
                    <option value="">All Status</option>
                    <option value="verified">Accepted</option>
                    <option value="unverified">Pending Acceptance</option>
                    <option value="cancelled">Rejected ({rejectedCorrections.length})</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Sort By</label>
                  <select
                    value={correctionsSortBy}
                    onChange={(e) => setCorrectionsSortBy(e.target.value)}
                    className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-500"
                  >
                    <option value="date">Date (Recent First)</option>
                    <option value="date_asc">Date (Oldest First)</option>
                    <option value="zone">Zone</option>
                    <option value="supervisor">Supervisor</option>
                  </select>
                </div>
                <div className="ml-auto">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">&nbsp;</label>
                  <button
                    onClick={handleDownloadCorrections}
                    disabled={downloadingCorrections || (correctionsFilterVerified === 'cancelled' ? rejectedCorrections.length === 0 : correctionsData.length === 0)}
                    className="h-10 px-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-semibold flex items-center gap-2"
                    data-testid="download-corrections-btn"
                  >
                    {downloadingCorrections ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Download Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Corrections Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {correctionsLoading ? (
                <div className="flex items-center justify-center p-12">
                  <RefreshCw className="w-8 h-8 text-orange-600 animate-spin" />
                </div>
              ) : correctionsFilterVerified === 'cancelled' ? (
                /* Rejected Corrections Table */
                rejectedCorrections.length === 0 ? (
                  <div className="p-12 text-center">
                    <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-bold text-lg text-slate-600">No Rejected Corrections</h3>
                    <p className="text-slate-500">No corrections have been rejected for this period</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto pb-20">
                    <table className="w-full">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-3 text-center text-sm font-bold text-slate-700 w-10">#</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Worker</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Park</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Zone</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Div</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Corrected By</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Submitted At</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Reason</th>
                          <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Photo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {rejectedCorrections.map((item, idx) => (
                          <tr key={idx} className="hover:bg-red-50">
                            <td className="px-2 py-3 text-center text-sm">{idx + 1}</td>
                            <td className="px-4 py-3 text-sm font-semibold">{formatDateDDMMYYYY(item.date)}</td>
                            <td className="px-4 py-3">
                              <div className="font-semibold text-blue-600">{item.employee_name}</div>
                              <div className="text-xs text-slate-500">{item.category}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-600">{item.park_name}</td>
                            <td className="px-4 py-3 text-sm">Zone {item.zone}</td>
                            <td className="px-4 py-3 text-sm">{item.division}</td>
                            <td className="px-4 py-3 text-sm text-indigo-600 font-medium">{item.corrected_by_name || item.corrected_by_id || '-'}</td>
                            <td className="px-4 py-3 text-xs text-slate-500">
                              {item.original_correction_timestamp ? new Date(item.original_correction_timestamp).toLocaleString('en-IN', { 
                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                              }) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                {item.cancel_reason || 'Admin rejected'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.correction_photo_url && (
                                <button
                                  onClick={() => setViewingPhoto(item.correction_photo_url)}
                                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold inline-flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : correctionsData.length === 0 ? (
                <div className="p-12 text-center">
                  <UserCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-bold text-lg text-slate-600">No Corrections Found</h3>
                  <p className="text-slate-500">No attendance corrections have been made for this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-20">
                  <table className="w-full">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-3 text-center text-sm font-bold text-slate-700 w-10">#</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Worker</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Park</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Zone</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Div</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Corrected By</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Submitted At</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Photo</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-slate-700">Accepted</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-slate-700 bg-orange-50">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(() => {
                        // Sort corrections based on selected sort option
                        const sorted = [...correctionsData].sort((a, b) => {
                          switch (correctionsSortBy) {
                            case 'date':
                              return (b.date || '').localeCompare(a.date || '');
                            case 'date_asc':
                              return (a.date || '').localeCompare(b.date || '');
                            case 'zone':
                              const zoneA = parseInt(a.zone) || 999;
                              const zoneB = parseInt(b.zone) || 999;
                              return zoneA - zoneB || (a.date || '').localeCompare(b.date || '');
                            case 'supervisor':
                              return (a.corrected_by_name || '').localeCompare(b.corrected_by_name || '') || (b.date || '').localeCompare(a.date || '');
                            default:
                              return 0;
                          }
                        });
                        
                        let lastDate = null;
                        let rowNum = 0;
                        
                        // Group corrections by date first
                        const groupedByDate = {};
                        sorted.forEach(correction => {
                          const date = correction.date || 'unknown';
                          if (!groupedByDate[date]) groupedByDate[date] = [];
                          groupedByDate[date].push(correction);
                        });
                        
                        const toggleDate = (date) => {
                          setCollapsedDates(prev => ({
                            ...prev,
                            [date]: !prev[date]
                          }));
                        };
                        
                        const collapseAll = () => {
                          const all = {};
                          Object.keys(groupedByDate).forEach(d => all[d] = true);
                          setCollapsedDates(all);
                        };
                        
                        const expandAll = () => setCollapsedDates({});
                        
                        // Format date as DD/MM/YYYY
                        const formatDate = (dateStr) => {
                          if (!dateStr) return '-';
                          const [y, m, d] = dateStr.split('-');
                          return `${d}/${m}/${y}`;
                        };
                        
                        return (
                          <>
                            {/* Collapse/Expand buttons */}
                            <tr className="bg-slate-200">
                              <td colSpan="10" className="px-4 py-2">
                                <div className="flex items-center gap-4">
                                  <span className="text-sm font-bold text-slate-600">Group by Date:</span>
                                  <button 
                                    onClick={collapseAll} 
                                    className="text-xs px-2 py-1 bg-slate-300 hover:bg-slate-400 rounded font-semibold"
                                  >
                                    Collapse All
                                  </button>
                                  <button 
                                    onClick={expandAll} 
                                    className="text-xs px-2 py-1 bg-slate-300 hover:bg-slate-400 rounded font-semibold"
                                  >
                                    Expand All
                                  </button>
                                  <span className="text-xs text-slate-500">
                                    ({Object.keys(groupedByDate).length} dates)
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {Object.entries(groupedByDate).map(([date, corrections]) => {
                              const isCollapsed = collapsedDates[date];
                              const dateRowStart = rowNum + 1;
                              
                              return (
                                <React.Fragment key={date}>
                                  {/* Date Header - Clickable to toggle */}
                                  <tr 
                                    className="bg-orange-100 cursor-pointer hover:bg-orange-200"
                                    onClick={() => toggleDate(date)}
                                  >
                                    <td colSpan="10" className="px-4 py-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <ChevronDown className={`w-4 h-4 text-orange-600 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                                          <span className="font-bold text-orange-800">
                                            {formatDate(date)}
                                            {' - '}
                                            {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' })}
                                          </span>
                                          <span className="ml-2 text-sm text-orange-600">
                                            ({corrections.length} corrections)
                                          </span>
                                        </div>
                                        <span className="text-xs text-orange-500">
                                          {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                  
                                  {/* Correction rows - hidden if collapsed */}
                                  {!isCollapsed && corrections.map((correction, cidx) => {
                                    rowNum++;
                                    const isCurrentlyViewing = viewingCorrectionId === correction.id;
                                    
                                    return (
                                      <tr 
                                        key={correction.id || `${date}-${cidx}`}
                                        className={`transition-colors ${
                                          isCurrentlyViewing 
                                            ? 'bg-blue-200 ring-2 ring-blue-500 ring-inset' 
                                            : 'hover:bg-slate-50'
                                        }`}
                                      >
                                        <td className="px-2 py-3 text-center">
                                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                            isCurrentlyViewing 
                                              ? 'bg-blue-600 text-white' 
                                              : 'bg-slate-200 text-slate-600'
                                          }`}>
                                            {rowNum}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-semibold whitespace-nowrap">{formatDate(correction.date)}</td>
                                        <td className="px-4 py-3">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setViewingWorkerPhotos({ employee_id: correction.employee_id, employee_name: correction.employee_name }); }}
                                            className="text-left hover:bg-blue-50 p-1 -m-1 rounded transition-colors"
                                            title="Click to view all photos for this worker"
                                          >
                                            <div className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline decoration-dotted">
                                              {correction.employee_name}
                                            </div>
                                            <div className="text-xs text-slate-500">{correction.category}</div>
                                          </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/calendar?park_id=${correction.park_id}`); }}
                                            className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                            title={`View attendance calendar for ${correction.park_name}`}
                                          >
                                            {correction.park_name}
                                          </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{correction.zone}</td>
                                        <td className="px-4 py-3 text-sm">{correction.division || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-purple-700">{correction.corrected_by_name}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">
                                          {correction.correction_timestamp ? new Date(correction.correction_timestamp).toLocaleString('en-IN', { 
                                            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                                          }) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          {correction.correction_photo_url ? (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setViewingPhoto(correction.correction_photo_url);
                                                setViewingCorrectionId(correction.id);
                                              }}
                                              className="flex items-center justify-center gap-1 px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700"
                                            >
                                              <ExternalLink className="w-4 h-4" />
                                              <span className="text-xs">View</span>
                                            </button>
                                          ) : (
                                            <span className="text-slate-300 text-xs">No photo</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          {correction.admin_verified ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                                              <Check className="w-3 h-3" /> Accepted
                                            </span>
                                          ) : (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleVerifyToggle(correction.id, correction.admin_verified); }}
                                              disabled={verifyingId === correction.id}
                                              className="px-3 py-1 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 text-xs font-bold disabled:opacity-50"
                                            >
                                              {verifyingId === correction.id ? 'Accepting...' : 'Accept'}
                                            </button>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center bg-orange-50">
                                          {correction.admin_cancelled_correction ? (
                                            <span className="px-2 py-1 rounded bg-slate-200 text-slate-600 text-xs font-bold">
                                              Rejected
                                            </span>
                                          ) : (
                                            <button
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                if (window.confirm(`Reject correction for ${correction.employee_name}? This will revert status to Absent.`)) {
                                                  try {
                                                    const response = await adminAPI.cancelSupervisorCorrection(correction.id, 'Admin rejected');
                                                    if (response.data.already_cancelled) {
                                                      toast.info('This correction was already rejected');
                                                    } else if (response.data.already_absent) {
                                                      toast.info('Attendance is already marked as Absent');
                                                    } else {
                                                      toast.success('Correction rejected successfully');
                                                    }
                                                    // Refresh corrections data
                                                    fetchCorrections();
                                                  } catch (err) {
                                                    const errorMsg = err.response?.data?.detail || 'Failed to reject correction';
                                                    toast.error(errorMsg);
                                                  }
                                                }
                                              }}
                                              className="px-3 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold"
                                            >
                                              Reject
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual Supervisor Attendance Modal */}
      {showSupervisorAttendanceModal && selectedSupervisorForAttendance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="bg-indigo-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Edit2 className="w-5 h-5" />
                  Manual Supervisor Attendance
                </h2>
                <p className="text-sm opacity-90">{selectedSupervisorDate}</p>
              </div>
              <button
                onClick={() => setShowSupervisorAttendanceModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Supervisor Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-bold text-lg">{selectedSupervisorForAttendance.supervisor_name}</p>
                <p className="text-sm text-slate-500">{selectedSupervisorForAttendance.supervisor_id}</p>
                <p className="text-sm text-slate-600 mt-1">
                  Assigned Parks: {selectedSupervisorForAttendance.assigned_parks}
                </p>
              </div>

              {/* Status Selection */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Attendance Status</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSupervisorAttendanceStatus('P')}
                    className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                      supervisorAttendanceStatus === 'P'
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-green-100'
                    }`}
                  >
                    Present (P)
                  </button>
                  <button
                    onClick={() => setSupervisorAttendanceStatus('A')}
                    className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                      supervisorAttendanceStatus === 'A'
                        ? 'bg-red-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-red-100'
                    }`}
                  >
                    Absent (A)
                  </button>
                  <button
                    onClick={() => setSupervisorAttendanceStatus('L')}
                    className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                      supervisorAttendanceStatus === 'L'
                        ? 'bg-orange-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-orange-100'
                    }`}
                  >
                    Leave (L)
                  </button>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Remarks (Optional)</label>
                <textarea
                  value={supervisorAttendanceRemarks}
                  onChange={(e) => setSupervisorAttendanceRemarks(e.target.value)}
                  placeholder="Enter reason for manual marking..."
                  className="w-full h-24 px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0 resize-none"
                />
              </div>
            </div>

            <div className="p-4 border-t bg-slate-50 flex items-center justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => setShowSupervisorAttendanceModal(false)}
                className="px-6 py-2 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSupervisorAttendance}
                disabled={savingSupervisorAttendance}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
              >
                {savingSupervisorAttendance ? 'Saving...' : 'Save Attendance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Worker Photos Gallery Modal - Shows all photos for a specific worker */}
      {viewingWorkerPhotos && (
        <div 
          className="fixed inset-0 bg-black/90 flex flex-col z-50"
          onClick={() => setViewingWorkerPhotos(null)}
        >
          <div className="bg-white p-4 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                All Correction Photos for: {viewingWorkerPhotos?.employee_name || 'Worker'}
              </h2>
              <p className="text-sm text-slate-500">
                Review all photos submitted for this worker across different dates to verify consistency
              </p>
            </div>
            <button
              onClick={() => setViewingWorkerPhotos(null)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            {(() => {
              // Filter by BOTH employee_id AND employee_name to avoid mixing photos from different workers
              const workerPhotos = correctionsData
                .filter(c => c.employee_id === viewingWorkerPhotos?.employee_id && 
                            c.employee_name === viewingWorkerPhotos?.employee_name && 
                            c.correction_photo_url)
                .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
              
              if (workerPhotos.length === 0) {
                return (
                  <div className="text-center text-white py-12">
                    <Image className="w-16 h-16 mx-auto opacity-50 mb-4" />
                    <p>No photos found for this worker</p>
                  </div>
                );
              }
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {workerPhotos.map((correction, idx) => {
                    const formatDateShort = (dateStr) => {
                      if (!dateStr) return '-';
                      const [y, m, d] = dateStr.split('-');
                      return `${d}/${m}/${y}`;
                    };
                    
                    return (
                      <div 
                        key={correction.id || idx}
                        className="bg-white rounded-xl overflow-hidden shadow-lg"
                      >
                        <div className="aspect-square relative">
                          <img 
                            src={correction.correction_photo_url} 
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => {
                              setViewingPhoto(correction.correction_photo_url);
                              setViewingCorrectionId(correction.id);
                            }}
                          />
                          <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm font-bold">
                            #{idx + 1}
                          </div>
                          {correction.admin_verified && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Accepted
                            </div>
                          )}
                        </div>
                        <div className="p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800">{formatDateShort(correction.date)}</span>
                            <span className="text-xs text-slate-500">Zone {correction.zone}</span>
                          </div>
                          <div className="text-sm text-slate-600 truncate" title={correction.park_name}>
                            {correction.park_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            By: {correction.corrected_by_name}
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleVerifyToggle(correction.id, correction.admin_verified)}
                              disabled={verifyingId === correction.id}
                              className={`flex-1 py-1 px-2 rounded text-xs font-bold transition-colors ${
                                correction.admin_verified 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                            >
                              {correction.admin_verified ? 'Accepted' : 'Accept'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          
          <div className="bg-white p-4 text-center text-sm text-slate-600" onClick={(e) => e.stopPropagation()}>
            Click any photo to view full size. Look for inconsistencies - different faces may indicate fraud.
          </div>
        </div>
      )}

      {/* Pending Worker Attendance View */}
      {viewMode === 'pending-worker' && (
        <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
          {/* Header / Info */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div>
              <h2 className="text-xl font-bold">Pending Worker Attendance</h2>
              <p className="text-sm text-slate-500">Same format and data as the Pending Attendance tab in Supervisor Login</p>
            </div>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 mb-2 block">Supervisor</label>
                <select
                  value={selectedSupervisor}
                  onChange={(e) => setSelectedSupervisor(e.target.value)}
                  className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-amber-600 focus:ring-0"
                  data-testid="pending-worker-supervisor-select"
                >
                  <option value="">Select Supervisor</option>
                  {supervisorsList.map((s) => (
                    <option key={s.supervisor_id} value={s.supervisor_id}>
                      {s.supervisor_name} ({s.supervisor_id})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-bold text-slate-700 mb-2 block">Month</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => changePendingWorkerMonth(-1)}
                    className="h-12 w-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 h-12 px-4 rounded-lg border-2 border-slate-300 flex items-center justify-center font-semibold">
                    {pendingWorkerMonthName}
                  </div>
                  <button
                    onClick={() => changePendingWorkerMonth(1)}
                    className="h-12 w-12 rounded-lg border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Legend & Summary */}
          {selectedSupervisor && pendingWorkerData && (
            <>
              {/* Summary Card */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {pendingWorkerData.summary?.corrected || 0}
                    </div>
                    <div className="text-sm text-slate-500 font-semibold">CORRECTIONS DONE</div>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-center gap-4 text-xs flex-wrap">
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-green-700 font-bold border">P</div>
                    <span>Present</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center text-red-700 font-bold border">A</div>
                    <span>Absent</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-6 bg-yellow-100 rounded flex items-center justify-center text-yellow-700 font-bold border">-</div>
                    <span>Not Visited</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-blue-700 font-bold border">P</div>
                    <span>Corrected</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Data Grid */}
          {pendingWorkerLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Loading pending worker attendance...</p>
            </div>
          ) : !selectedSupervisor ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-600">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="font-semibold text-lg">Please select a supervisor to view records</p>
            </div>
          ) : pendingWorkerData && (!pendingWorkerData.parks || pendingWorkerData.parks.length === 0) ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-600">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="font-semibold text-lg">No Parks Assigned or Pending Attendance</p>
              <p className="text-slate-400 mt-1">This supervisor has no assigned parks for this month</p>
            </div>
          ) : pendingWorkerData ? (
            <div className="space-y-4">
              {pendingWorkerData.parks.map((park) => {
                const dayHeaders = [];
                for (let d = 1; d <= (pendingWorkerData.last_day || 0); d++) {
                  dayHeaders.push(d);
                }

                return (
                  <div key={park.park_id} className="bg-white rounded-lg shadow-sm overflow-hidden border">
                    {/* Park Header */}
                    <button
                      onClick={() => togglePendingWorkerPark(park.park_id)}
                      className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800">{park.park_name}</span>
                        <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Zone {park.zone}</span>
                        {park.division && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Div {park.division}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{park.workers?.length || 0} workers</span>
                        <ChevronRight className={`w-4 h-4 transition-transform ${pendingWorkerExpandedParks[park.park_id] ? 'rotate-90' : ''}`} />
                      </div>
                    </button>

                    {/* Workers Table */}
                    {pendingWorkerExpandedParks[park.park_id] && park.workers && park.workers.length > 0 && (
                      <div className="overflow-x-auto border-t">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-800 text-white">
                              <th className="p-2 text-left sticky left-0 bg-slate-800 min-w-[150px] z-10 border-b">Worker</th>
                              {dayHeaders.map(d => (
                                <th key={d} className="p-2 text-center min-w-[40px] border-b">{d}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {park.workers.map((worker, idx) => (
                              <tr key={worker.employee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                                <td className="p-2 sticky left-0 bg-inherit border-r border-b font-medium z-10">
                                  <div className="font-semibold text-slate-800 text-xs truncate max-w-[140px]" title={worker.employee_name}>
                                    {worker.employee_name}
                                  </div>
                                  <div className="text-[10px] text-slate-400">{worker.category}</div>
                                </td>
                                {dayHeaders.map(d => {
                                  const cellData = worker.attendance?.[String(d)];
                                  // Map to cell coloring classes (read-only style, identical classes to SupervisorPendingAttendance)
                                  let cellClass = 'bg-slate-100 border-b border-r text-slate-400';
                                  if (cellData) {
                                    const isAdminEdited = cellData.manually_edited === true;
                                    if (isAdminEdited) {
                                      if (cellData.status === 'A') {
                                        cellClass = 'bg-red-300 text-red-900 border-b border-r border-2 border-red-500';
                                      } else if (cellData.status === 'P') {
                                        cellClass = 'bg-blue-200 text-blue-800 border-b border-r border-2 border-blue-500';
                                      }
                                    } else if (cellData.corrected) {
                                      cellClass = 'bg-blue-100 text-blue-700 border-b border-r font-bold';
                                    } else if (cellData.status === 'P') {
                                      cellClass = 'bg-green-100 text-green-700 border-b border-r font-bold';
                                    } else if (cellData.status === 'A') {
                                      cellClass = 'bg-red-100 text-red-700 border-b border-r font-bold';
                                    } else if (cellData.status === '-') {
                                      cellClass = 'bg-yellow-100 text-yellow-700 border-b border-r font-bold';
                                    }
                                  }

                                  return (
                                    <td key={d} className={`p-1 text-center text-xs ${cellClass}`}>
                                      <span className="font-bold">
                                        {cellData?.status || '-'}
                                      </span>
                                      {cellData?.corrected && cellData.status !== 'A' && (
                                        <CheckCircle2 className="w-3 h-3 mx-auto text-blue-600 mt-0.5" />
                                      )}
                                      {cellData?.manually_edited && (
                                        <span className="text-[9px] font-bold block text-blue-600 mt-0.5" title="Admin edited">✎</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {/* Photo Viewer Modal */}
      {viewingPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setViewingPhoto(null)}
              className="absolute -top-10 right-0 p-2 bg-white/20 rounded-full hover:bg-white/40"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img 
              src={viewingPhoto} 
              alt="Correction proof" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCalendar;
